import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { FileText, Receipt, Landmark, Wallet, BookOpen, Plus, MoreVertical, CreditCard, ArrowUpRight, TrendingUp, TrendingDown, Layers, Building2 } from 'lucide-react';
import { motion } from 'motion/react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatCurrency, getDefaultCurrency } from '../lib/currency';

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');


interface AccountingDashboardViewProps {
  orgId: string;
  settings: any;
  onNavigate: (tab: string, id?: string) => void;
}

export default function AccountingDashboardView({ orgId, settings, onNavigate }: AccountingDashboardViewProps) {
  const [invoicesStat, setInvoicesStat] = useState({ toValidate: 0, unpaid: 0, amount: 0 });
  const [billsStat, setBillsStat] = useState({ toValidate: 0, unpaid: 0, amount: 0 });
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [journalsStat, setJournalsStat] = useState({ draft: 0, amount: 0 });

  useEffect(() => {
    if (!orgId) return;

    const appAuthToken = getLocalAuthToken();
    if (appAuthToken) {
      let cancelled = false;
      const summarizeDocuments = (documents: any[]) => documents.reduce(
        (summary, document) => {
          if (document.status === 'Draft') summary.toValidate += 1;
          if (document.status === 'Awaiting Payment' || document.status === 'Partially Paid') {
            summary.unpaid += 1;
            summary.amount += Number(document.totalAmount || 0) - Number(document.paymentReceived || 0);
          }
          return summary;
        },
        { toValidate: 0, unpaid: 0, amount: 0 }
      );

      void Promise.all([
        fetch(`/api/organizations/${orgId}/documents?types=invoice`, { headers: { Authorization: `Bearer ${appAuthToken}` } }),
        fetch(`/api/organizations/${orgId}/documents?types=bill`, { headers: { Authorization: `Bearer ${appAuthToken}` } }),
        fetch(`/api/organizations/${orgId}/manual-journals`, { headers: { Authorization: `Bearer ${appAuthToken}` } }),
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers: { Authorization: `Bearer ${appAuthToken}` } })
      ])
        .then(async responses => {
          if (responses.some(response => !response.ok)) throw new Error('Failed to load accounting dashboard data');
          return Promise.all(responses.map(response => response.json()));
        })
        .then(([invoices, bills, journals, banks]) => {
          if (cancelled) return;
          setInvoicesStat(summarizeDocuments(Array.isArray(invoices) ? invoices : []));
          setBillsStat(summarizeDocuments(Array.isArray(bills) ? bills : []));
          const draftJournals = (Array.isArray(journals) ? journals : []).filter(journal => journal.status === 'draft' || journal.status === 'Draft');
          setJournalsStat({
            draft: draftJournals.length,
            amount: draftJournals.reduce((total, journal) => total + (journal.lines || []).reduce((lineTotal: number, line: any) => lineTotal + Number(line.debit || 0), 0), 0)
          });
          setBankAccounts(Array.isArray(banks) ? banks : []);
        })
        .catch(error => {
          if (!cancelled) {
            console.error('Local API error loading accounting dashboard:', error);
            setInvoicesStat({ toValidate: 0, unpaid: 0, amount: 0 });
            setBillsStat({ toValidate: 0, unpaid: 0, amount: 0 });
            setJournalsStat({ draft: 0, amount: 0 });
            setBankAccounts([]);
          }
        });
      return () => { cancelled = true; };
    }

    // Invoices listener
    const unsubscribeInvoices = onSnapshot(query(collection(db, 'invoices'), where('orgId', '==', orgId)), (snap) => {
      let toValidate = 0;
      let unpaid = 0;
      let amount = 0;
      snap.forEach(doc => {
        const data = doc.data();
        if (data.status === 'Draft') toValidate++;
        if (data.status === 'Awaiting Payment' || data.status === 'Partially Paid') {
          unpaid++;
          amount += (data.totalAmount - (data.amountPaid || 0));
        }
      });
      setInvoicesStat({ toValidate, unpaid, amount });
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'invoices');
    });

    // Bills listener
    const unsubscribeBills = onSnapshot(query(collection(db, 'bills'), where('orgId', '==', orgId)), (snap) => {
      let toValidate = 0;
      let unpaid = 0;
      let amount = 0;
      snap.forEach(doc => {
        const data = doc.data();
        if (data.status === 'Draft') toValidate++;
        if (data.status === 'Awaiting Payment' || data.status === 'Partially Paid') {
          unpaid++;
          amount += (data.totalAmount - (data.amountPaid || 0));
        }
      });
      setBillsStat({ toValidate, unpaid, amount });
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'bills');
    });

    // Journals listener
    const unsubscribeJournals = onSnapshot(query(collection(db, 'manual_journals'), where('orgId', '==', orgId)), (snap) => {
      let draft = 0;
      let amount = 0;
      snap.forEach(doc => {
        const data = doc.data();
        if (data.status === 'Draft') {
          draft++;
          amount += (data.totalDebit || 0);
        }
      });
      setJournalsStat({ draft, amount });
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'manual_journals');
    });

    // Bank accounts listener
    const unsubscribeBanks = onSnapshot(query(collection(db, 'bank_accounts'), where('orgId', '==', orgId)), (snap) => {
      const banks = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBankAccounts(banks);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'bank_accounts');
    });

    return () => {
      unsubscribeInvoices();
      unsubscribeBills();
      unsubscribeJournals();
      unsubscribeBanks();
    };
  }, [orgId]);

  const currency = getDefaultCurrency(settings);

  const CardHeader = ({ title, subtitle, icon: Icon, colorClass }: { title: string, subtitle?: string, icon?: any, colorClass?: string }) => (
    <div className="flex items-start justify-between border-b border-border pb-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className={`p-2.5 rounded-xl ${colorClass || 'bg-muted text-muted-foreground'}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div>
          <h3 className="text-base font-bold tracking-tight text-foreground">{title}</h3>
          {subtitle && <p className="text-xs font-medium text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <button type="button" aria-label={`More options for ${title}`} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
        <MoreVertical className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-8 py-6">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-2 sm:px-0">
        <div>
          <h2 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight text-foreground">
            Accounting Command Hub
            <Badge variant="success">Live sync</Badge>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Real-time journal ledgers, unpaid document pipelines, and treasury status</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => onNavigate('invoices')}>
            <Plus className="h-4 w-4" /> New transaction
          </Button>
        </div>
      </div>

      {/* Grid of Journal Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-2 sm:px-0">
        {/* Customer Invoices Card */}
        <motion.div 
          whileHover={{ y: -4 }}
          className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <CardHeader title="Customer Invoices" subtitle="Sales Journal" icon={FileText} colorClass="bg-muted text-muted-foreground" />
            <div className="mt-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Unpaid</span>
                <span className="text-xl font-extrabold text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatCurrency(invoicesStat.amount, currency)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="rounded-lg border border-border bg-muted/50 p-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">To Validate</p>
                  <p className="mt-0.5 text-lg font-bold text-foreground">{invoicesStat.toValidate}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-800">
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Awaiting Pay</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-0.5">{invoicesStat.unpaid}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
            <button 
              onClick={() => onNavigate('invoices')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Open Invoices <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => onNavigate('invoices')}
              className="rounded-md bg-muted p-2 text-foreground transition-colors hover:bg-border"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Vendor Bills Card */}
        <motion.div 
          whileHover={{ y: -4 }}
          className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <CardHeader title="Vendor Bills" subtitle="Purchase Journal" icon={Receipt} colorClass="bg-muted text-muted-foreground" />
            <div className="mt-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Outstanding</span>
                <span className="text-xl font-extrabold text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatCurrency(billsStat.amount, currency)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="rounded-lg border border-border bg-muted/50 p-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">To Validate</p>
                  <p className="mt-0.5 text-lg font-bold text-foreground">{billsStat.toValidate}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-800">
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Bills to Pay</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-0.5">{billsStat.unpaid}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
            <button 
              onClick={() => onNavigate('bills')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Open Bills <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => onNavigate('bills')}
              className="rounded-md bg-muted p-2 text-foreground transition-colors hover:bg-border"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Miscellaneous Operations Card */}
        <motion.div 
          whileHover={{ y: -4 }}
          className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <CardHeader title="Miscellaneous Operations" subtitle="General Journal" icon={BookOpen} colorClass="bg-muted text-muted-foreground" />
            <div className="mt-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Draft Volume</span>
                <span className="text-xl font-extrabold text-slate-900 dark:text-slate-100 tabular-nums">
                  {formatCurrency(journalsStat.amount, currency)}
                </span>
              </div>

              <div className="mt-2 rounded-lg border border-border bg-muted/50 p-3">
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Pending Draft Entries</p>
                <p className="mt-0.5 text-lg font-bold text-foreground">{journalsStat.draft} Draft Entries</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
            <button 
              onClick={() => onNavigate('manual_journal')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Journal Ledger <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => onNavigate('manual_journal')}
              className="rounded-md bg-muted p-2 text-foreground transition-colors hover:bg-border"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Dynamic Bank Account Cards */}
        {bankAccounts.map(bank => (
          <motion.div 
            key={bank.id} 
            whileHover={{ y: -4 }}
            className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
          >
            <div>
              <CardHeader title={bank.accountName} subtitle={`Bank Account • ${bank.accountNumber}`} icon={Landmark} colorClass="bg-muted text-muted-foreground" />
              <div className="mt-5 space-y-4">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ledger Balance</span>
                  <span className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 tabular-nums mt-1">
                    {formatCurrency(bank.balance || 0, bank.currency || currency)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
              <button 
                onClick={() => onNavigate('bank_accounts', bank.id)}
                className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
              >
                Reconcile & Statements <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        ))}

        {/* Fixed Assets Quick Link */}
        <motion.div 
          whileHover={{ y: -4 }}
          className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <CardHeader title="Fixed Assets & Registers" subtitle="Asset Depreciation" icon={Building2} colorClass="bg-muted text-muted-foreground" />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
              Track equipment depreciation, asset classes, and annual straight-line allocation schedules.
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between">
            <button 
              onClick={() => onNavigate('fixed_assets')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Go to Fixed Assets <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>

        {/* Customer & Vendor Balances Quick Link */}
        <motion.div 
          whileHover={{ y: -4 }}
          className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
        >
          <div>
            <CardHeader title="Customer & Supplier Balances" subtitle="Sub-ledger Analytics" icon={Wallet} colorClass="bg-muted text-muted-foreground" />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 leading-relaxed">
              Review aged receivables, payable statement of accounts, and partner ledger balances.
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between gap-2">
            <button 
              onClick={() => onNavigate('customer_balances')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Receivables <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => onNavigate('vendor_balances')}
              className="text-xs font-semibold text-foreground hover:text-muted-foreground flex items-center gap-1 transition-colors"
            >
              Payables <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
